import React, { useMemo } from "react";
import { IconClock, IconCertificate, IconAlertTriangle, IconUserCircle } from "@tabler/icons-react";

// ---- i18n ----
const dict = {
    en: {
        expires: "Expires",
        validFrom: "Valid from",
        issuer: "Issued by",
        notify: "Notify level",
        contact: "Contact group",
        status: "Status",
        error: "Last error",
        domain: "Domain",
        certificate: "Certificate",
        daysLeft: (d) => `${d} days left`,
        expired: "Expired",
        unknown: "Unknown",
        notificationOn: "Notifications on",
        notificationOff: "Notifications off",
        meta: "Meta",
        hash: "Hash",
        company: "Company",
        site: "Site",
        validity: "Validity",
        tech: "Technical",
        raw: "Raw data",
        id: "ID",
        statusCode: "Status code",
        notifyFlag: "Notify flag",
        notifyLevel: "Notify level",
        contactGroup: "Contact group",
        hidden: "Hidden",
        history: "History",
        event: "Event",
        before: "Before",
        after: "After",
        historyDate: "Date",
        noHistory: "No history records",
        warnings: "Alerts",
        warnSoon: (d) => `Expires in ${d} days`,
        warnExpired: (d) => `Expired ${Math.abs(d)} days ago`,
        warnOk: "All good: no warnings",
        warnExpiredShort: "Unfortunately, the certificate is expired",
        contactCard: "Contact group",
    },
    ru: {
        expires: "Истекает",
        validFrom: "Действует с",
        issuer: "Выдан",
        notify: "Уровень уведомлений",
        contact: "Группа контактов",
        status: "Статус",
        error: "Последняя ошибка",
        domain: "Домен",
        certificate: "Сертификат",
        daysLeft: (d) => `Осталось ${d} дн.`,
        expired: "Истек",
        unknown: "Неизвестно",
        notificationOn: "Уведомления включены",
        notificationOff: "Уведомления отключены",
        meta: "Дополнительно",
        hash: "Хэш",
        company: "Компания",
        site: "Сайт",
        validity: "Период действия",
        tech: "Техническое",
        raw: "Сырые данные",
        id: "ID",
        statusCode: "Код статуса",
        notifyFlag: "Флаг уведомлений",
        notifyLevel: "Уровень уведомлений",
        contactGroup: "Группа контактов",
        hidden: "Скрыт",
        history: "История",
        event: "Событие",
        before: "До",
        after: "После",
        historyDate: "Дата",
        noHistory: "Записей нет",
        warnings: "Предупреждения",
        warnSoon: (d) => `Истекает через ${d} дн.`,
        warnExpired: (d) => `Истёк ${Math.abs(d)} дн. назад`,
        warnOk: "Всё хорошо: предупреждений нет",
        warnExpiredShort: "К сожалению, сертификат просрочен",
        contactCard: "Группа контактов",
    },
    ua: {
        expires: "Спливає",
        validFrom: "Діє з",
        issuer: "Видано",
        notify: "Рівень сповіщень",
        contact: "Група контактів",
        status: "Статус",
        error: "Остання помилка",
        domain: "Домен",
        certificate: "Сертифікат",
        daysLeft: (d) => `Залишилось ${d} дн.`,
        expired: "Строк минув",
        unknown: "Невідомо",
        notificationOn: "Сповіщення увімкнено",
        notificationOff: "Сповіщення вимкнено",
        meta: "Додатково",
        hash: "Хеш",
        company: "Компанія",
        site: "Сайт",
        validity: "Період дії",
        tech: "Технічне",
        raw: "Сирі дані",
        id: "ID",
        statusCode: "Код статусу",
        notifyFlag: "Прапор сповіщень",
        notifyLevel: "Рівень сповіщень",
        contactGroup: "Група контактів",
        hidden: "Приховано",
        history: "Історія",
        event: "Подія",
        before: "Було",
        after: "Стало",
        historyDate: "Дата",
        noHistory: "Записів немає",
        warnings: "Попередження",
        warnSoon: (d) => `Спливає через ${d} дн.`,
        warnExpired: (d) => `Сплив ${Math.abs(d)} дн. тому`,
        warnOk: "Усе гаразд: попереджень немає",
        warnExpiredShort: "На жаль, сертифікат прострочено",
        contactCard: "Група контактів",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const Dashboard = ({ lang: langProp, singleArgs }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const details = singleArgs?.details || {};
    const logs = singleArgs?.logs || [];

    const formatDate = (date) => {
        if (!date) return t.unknown;
        const d = new Date(date);
        return Number.isNaN(d.getTime()) ? t.unknown : d.toLocaleString();
    };

    const calcDaysLeft = (date) => {
        if (!date) return t.unknown;
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return t.unknown;
        const diff = d.getTime() - Date.now();
        const days = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        return diff <= 0 ? t.expired : t.daysLeft(days);
    };

    const calcProgress = (from, to) => {
        if (!from || !to) return 0;
        const start = new Date(from).getTime();
        const end = new Date(to).getTime();
        if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
        const now = Date.now();
        const ratio = Math.min(1, Math.max(0, (now - start) / (end - start)));
        return Math.round(ratio * 100);
    };

    const statusColors = {
        1: { bg: "rgba(46, 204, 113, 0.15)", text: "#1e7e34", label: "Active" },
        0: { bg: "rgba(231, 76, 60, 0.15)", text: "#c0392b", label: "Inactive" },
        3: { bg: "rgba(255, 193, 7, 0.2)", text: "#b45309", label: "Not found" },
    };
    const status = Number(details.status);
    const statusMeta = statusColors[status] || { bg: "rgba(99,102,241,0.15)", text: "#4338ca", label: t.unknown };

    const infoBlocks = [
        {
            label: t.issuer,
            value: details.issued_by ? details.issued_by : "Not Found",
            icon: <IconCertificate size={28} color="#111827" />,
        },
        {
            label: t.validFrom,
            value: formatDate(details.date_from),
            icon: <IconClock size={28} color="#0ea5e9" />,
        },
        {
            label: t.expires,
            value: formatDate(details.date_to),
            helper: calcDaysLeft(details.date_to),
            icon: <IconClock size={28} color="#9333ea" />,
        },
    ];

    const progress = calcProgress(details.date_from, details.date_to);
    const daysLeftNumeric = (() => {
        if (!details.date_to) return null;
        const d = new Date(details.date_to);
        if (Number.isNaN(d.getTime())) return null;
        return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    })();

    const parseComment = (comment) => {
        if (!comment) return null;
        try {
            return typeof comment === "string" ? JSON.parse(comment) : comment;
        } catch (e) {
            return null;
        }
    };

    const parsedLogs = logs.map((log) => {
        const payload = parseComment(log.comment) || {};
        return {
            ...log,
            event: payload.event || log.event || t.unknown,
            domain: payload.domain_name || details.domain_name || t.domain,
            before: payload.before || null,
            after: payload.after || null,
        };
    });

    const renderSnapshot = (snapshot) => {
        if (!snapshot) return <div className="text-muted">{t.unknown}</div>;

        const rows = [
            { label: t.validFrom, value: formatDate(snapshot.date_from) },
            { label: t.expires, value: formatDate(snapshot.date_to) },
            { label: t.issuer, value: snapshot.issued_by },
            { label: t.status, value: snapshot.error_code || snapshot.status || snapshot.status_text },
            { label: t.hash, value: snapshot.hash_code },
        ].filter((row) => row.value);

        return (
            <div className="small">
                {rows.map((row, idx) => (
                    <div key={`snap-${row.label}-${idx}`}>
                        <span className="text-muted">{row.label}:</span> <span className="fw-semibold">{row.value}</span>
                    </div>
                ))}
            </div>
        );
    };

    const heroStyle = {
        background:
            "linear-gradient(120deg, #e0f2fe 0%, #dbeafe 45%, #c7d2fe 100%), radial-gradient(circle at 15% 20%, rgba(255,255,255,0.8), transparent 35%), radial-gradient(circle at 85% 20%, rgba(255,255,255,0.6), transparent 35%)",
        color: "#0f172a",
        boxShadow: "0 12px 32px rgba(59,130,246,0.18)",
        border: "1px solid rgba(59,130,246,0.12)",
    };

    return (
        <div className="d-flex flex-column gap-4">
            <div
                className="rounded-4 p-4 shadow-sm"
                style={heroStyle}
            >
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                        <div className="text-uppercase fw-semibold small text-muted">{t.certificate}</div>
                        <h2 className="mb-3" style={{ textShadow: "0 3px 10px rgba(15,23,42,0.18)" }}>
                            {details.domain_name || t.domain}
                        </h2>
                        <div
                            className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
                            style={{ background: statusMeta.bg, color: statusMeta.text }}
                        >
                            <span
                                className="rounded-circle"
                                style={{ display: "inline-block", width: 10, height: 10, background: statusMeta.text }}
                            ></span>
                            {statusMeta.label}
                        </div>
                        {details.contacts_contact_name && (
                            <div className="mt-2 d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-white text-dark fw-semibold shadow-sm">
                                <IconUserCircle size={18} />
                                <span>{details.contacts_contact_name}</span>
                            </div>
                        )}
                    </div>
                    <div className="text-end">
                        <div className="text-uppercase fw-semibold small opacity-75">{t.expires}</div>
                        <div className="fs-3 fw-bold">{formatDate(details.date_to)}</div>
                        <div className="small opacity-75">{calcDaysLeft(details.date_to)}</div>
                    </div>
                </div>
                {progress > 0 && (
                    <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center small opacity-75 mb-1">
                            <span>{t.validity}</span>
                            <span>{progress}%</span>
                        </div>
                        <div className="progress" style={{ height: 10, background: "rgba(255,255,255,0.2)" }}>
                            <div
                                className="progress-bar bg-white"
                                role="progressbar"
                                style={{ width: `${progress}%` }}
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                    </div>
                )}
            </div>

            <div className="row g-3">
                {infoBlocks.filter((block) => block.label !== t.notify).map((item, idx) => (
                    <div className="col-12 col-md-6 col-xl-4" key={`info-${idx}`}>
                        <div className="border rounded-4 p-3 h-100 shadow-sm bg-white">
                            <div className="d-flex align-items-center gap-3 mb-2">
                                <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                                    {item.icon}
                                </div>
                                <div>
                                    <div className="text-uppercase text-muted fw-semibold small">{item.label}</div>
                                    <div className="fw-semibold">{item.value || t.unknown}</div>
                                    {item.helper && <div className="text-muted small">{item.helper}</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-3 align-items-stretch">
                <div className="col-12 col-md-4 col-xl-3">
                    <div className="border rounded-4 p-3 h-100 shadow-sm bg-white d-flex align-items-start gap-3">
                        <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                            <IconUserCircle size={28} color="#2563eb" />
                        </div>
                        <div>
                            <div className="text-uppercase text-muted fw-semibold small">{t.contactCard}</div>
                            <div className="fw-semibold">{details.contacts_group_name || details.contacts_contact_name || t.unknown}</div>
                            {details.contacts_email && <div className="small text-muted">{details.contacts_email}</div>}
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-8 col-xl-9">
                    <div
                        className={`rounded-4 p-3 h-100 shadow-sm d-flex align-items-center gap-3 ${daysLeftNumeric !== null && daysLeftNumeric < 11 ? "bg-warning-subtle" : "bg-light"}`}
                        style={{ border: "1px solid rgba(0,0,0,0.05)" }}
                    >
                        <div className="rounded-circle bg-white d-inline-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                            <IconAlertTriangle size={26} color={daysLeftNumeric !== null && daysLeftNumeric < 11 ? "#b45309" : "#16a34a"} />
                        </div>
                        <div>
                            <div className="text-uppercase fw-semibold small text-muted">{t.warnings}</div>
                            <div className="fw-semibold">
                                {daysLeftNumeric === null && t.warnOk}
                                {daysLeftNumeric !== null && daysLeftNumeric < 0 && t.warnExpiredShort}
                                {daysLeftNumeric !== null && daysLeftNumeric >= 0 && daysLeftNumeric < 11 && t.warnSoon(daysLeftNumeric)}
                                {daysLeftNumeric !== null && daysLeftNumeric >= 11 && t.warnOk}
                            </div>
                            {daysLeftNumeric !== null && (
                                <div className="small text-muted">
                                    {t.expires}: {formatDate(details.date_to)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border rounded-4 p-3 shadow-sm bg-white">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <IconClock size={20} />
                    <div className="fw-semibold text-uppercase small text-muted">{t.history}</div>
                </div>
                {parsedLogs.length === 0 ? (
                    <div className="text-muted">{t.noHistory}</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "20%" }}>{t.historyDate}</th>
                                    <th style={{ width: "20%" }}>{t.event}</th>
                                    <th style={{ width: "20%" }}>{t.domain}</th>
                                    <th style={{ width: "40%" }}>{t.after}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td className="small text-muted">{formatDate(log.created_date)}</td>
                                        <td className="fw-semibold">{log.event}</td>
                                        <td className="small text-muted">{log.domain}</td>
                                        <td>{renderSnapshot(log.after)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

        </div>
    );
};

export default Dashboard;
